import { useState } from 'react'
import { Alert, Box, Button, Group, Loader, Text, Textarea, TextInput } from '@mantine/core'
import {
  IconAlertTriangle,
  IconCheck,
  IconPlayerPause,
  IconPlayerPlay,
  IconX,
} from '@tabler/icons-react'
import type { AiDictate } from './useAiDictate'

/** Engine error codes → words a provider can act on. */
const SPEECH_ERROR_COPY: Record<string, string> = {
  'nothing-heard':
    "Nothing heard yet — check the browser's microphone permission and the input device.",
  'not-allowed': 'Microphone access is blocked — allow it in the browser and try again.',
  'audio-capture': 'No microphone was found — check the input device.',
  'microphone-unavailable': 'The microphone stopped — check the input device, then resume.',
  network: "The browser couldn't reach its speech service — check the connection.",
}

/**
 * The AI Dictate panel (PD-1516): recording context + Pause/Finish/Cancel
 * while capturing, then the AI-formatted preview with its medication review.
 * Fixed to the bottom of the viewport so the controls and live transcript stay
 * visible however deep in the note the target field is — the target
 * itself is outlined by the hook.
 */
export function AiDictateBar({ dictate }: { dictate: AiDictate }) {
  const [changingIndex, setChangingIndex] = useState<number | null>(null)
  const [changeText, setChangeText] = useState('')
  if (dictate.phase === 'idle') return null

  const targetLabel = dictate.target?.label ?? 'the note'
  const recordingLabel =
    dictate.mode === 'replacement'
      ? `Replacing selected text in ${targetLabel}`
      : dictate.mode === 'field'
        ? `Recording into: ${targetLabel}`
        : 'Recording — AI will organize your dictation into the note'
  const unresolved = dictate.medReviews.filter((m) => !m.resolved)
  const speechErrorCopy = dictate.speechError
    ? (SPEECH_ERROR_COPY[dictate.speechError] ?? `Microphone error: ${dictate.speechError}`)
    : null

  const submitChange = (index: number) => {
    dictate.changeMedication(index, changeText)
    setChangingIndex(null)
    setChangeText('')
  }

  return (
    <Box
      p="sm"
      style={{
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(720px, calc(100vw - 32px))',
        // Above the page content, below Mantine's modal (200) and notification layers.
        zIndex: 150,
        borderRadius: 8,
        border: '1px solid var(--mantine-color-blue-3)',
        background: 'var(--mantine-color-blue-0)',
        boxShadow: 'var(--mantine-shadow-lg)',
      }}
      data-testid="ai-dictate-bar"
    >
      {(dictate.phase === 'recording' || dictate.phase === 'paused') && (
        <>
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Group gap={8} wrap="nowrap">
              <Box
                w={8}
                h={8}
                style={{
                  borderRadius: '50%',
                  background:
                    dictate.phase === 'recording'
                      ? 'var(--mantine-color-red-6)'
                      : 'var(--mantine-color-gray-5)',
                }}
              />
              <Text size="sm" fw={600} c="blue.8" data-testid="ai-dictate-context">
                {dictate.phase === 'paused' ? `Paused — ${recordingLabel}` : recordingLabel}
              </Text>
            </Group>
            <Group gap="xs" wrap="nowrap">
              {dictate.phase === 'recording' ? (
                <Button
                  size="compact-sm"
                  variant="subtle"
                  leftSection={<IconPlayerPause size={14} />}
                  onClick={dictate.pause}
                  data-testid="ai-dictate-pause"
                >
                  Pause
                </Button>
              ) : (
                <Button
                  size="compact-sm"
                  variant="subtle"
                  leftSection={<IconPlayerPlay size={14} />}
                  onClick={dictate.resume}
                  data-testid="ai-dictate-resume"
                >
                  Resume
                </Button>
              )}
              <Button
                size="compact-sm"
                leftSection={<IconCheck size={14} />}
                onClick={() => void dictate.finishRecording()}
                data-testid="ai-dictate-finish"
              >
                Finish
              </Button>
              <Button
                size="compact-sm"
                variant="subtle"
                color="gray"
                leftSection={<IconX size={14} />}
                onClick={dictate.cancel}
                data-testid="ai-dictate-cancel"
              >
                Cancel
              </Button>
            </Group>
          </Group>
          {dictate.mode === 'replacement' && dictate.selection && (
            <Text size="xs" c="dimmed" mt={4} lineClamp={1}>
              Replacing: “{dictate.selection.text}”
            </Text>
          )}
          {dictate.liveText ? (
            <Text size="xs" c="dimmed" mt={6} lineClamp={2}>
              {dictate.liveText}
            </Text>
          ) : (
            dictate.phase === 'recording' && (
              <Text size="xs" c="dimmed" fs="italic" mt={6}>
                Listening — speak now…
              </Text>
            )
          )}
          {speechErrorCopy && (
            <Text size="xs" c="red.7" mt={4} data-testid="ai-dictate-speech-error">
              {speechErrorCopy}
            </Text>
          )}
        </>
      )}

      {dictate.phase === 'processing' && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="blue.8">
            Formatting your dictation…
          </Text>
        </Group>
      )}

      {dictate.phase === 'error' && (
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Text size="sm" c="red.8">
            {dictate.mode === 'organize'
              ? "Couldn't generate the note — your dictation is kept."
              : "Couldn't format the dictation — your speech is kept."}
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Button size="compact-sm" onClick={dictate.retryFormat} data-testid="ai-dictate-retry">
              Retry
            </Button>
            <Button
              size="compact-sm"
              variant="default"
              onClick={dictate.discardPreview}
              data-testid="ai-dictate-discard"
            >
              Discard
            </Button>
          </Group>
        </Group>
      )}

      {dictate.phase === 'preview' && dictate.preview !== null && (
        <>
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Text size="sm" fw={600} c="blue.8">
              {dictate.mode === 'replacement'
                ? `Replacement preview — ${targetLabel}`
                : `AI-formatted preview — ${targetLabel}`}
              <Text span size="xs" c="dimmed" ml={8}>
                Editable. Review before adding to the note.
              </Text>
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Button
                size="compact-sm"
                onClick={dictate.acceptPreview}
                disabled={unresolved.length > 0}
                data-testid="ai-dictate-accept"
              >
                {dictate.mode === 'replacement' ? 'Accept replacement' : 'Accept'}
              </Button>
              <Button
                size="compact-sm"
                variant="default"
                onClick={dictate.discardPreview}
                data-testid="ai-dictate-discard"
              >
                Discard
              </Button>
            </Group>
          </Group>
          <Textarea
            autosize
            minRows={2}
            maxRows={8}
            mt={6}
            value={dictate.preview}
            onChange={(e) => dictate.setPreview(e.currentTarget.value)}
            data-testid="ai-dictate-preview"
          />
          {dictate.medReviews.length > 0 && unresolved.length > 0 && (
            <Alert
              color="orange"
              mt={8}
              p="xs"
              icon={<IconAlertTriangle size={16} />}
              title="Review medications"
              data-testid="ai-dictate-med-review"
            >
              {dictate.medReviews.map((med, index) =>
                med.resolved ? null : (
                  <Group key={index} gap="xs" wrap="nowrap" mb={4}>
                    <Text size="sm" style={{ flex: 1 }}>
                      heard “{med.original}” →{' '}
                      <Text span fw={600}>
                        {med.normalized}
                      </Text>
                      {med.reason ? (
                        <Text span c="dimmed">
                          {' '}
                          — {med.reason}
                        </Text>
                      ) : null}
                    </Text>
                    {changingIndex === index ? (
                      <TextInput
                        size="xs"
                        value={changeText}
                        onChange={(e) => setChangeText(e.currentTarget.value)}
                        onKeyDown={(e) => e.key === 'Enter' && submitChange(index)}
                        onBlur={() => submitChange(index)}
                        autoFocus
                        w={190}
                        data-testid={`ai-dictate-med-change-input-${index}`}
                      />
                    ) : (
                      <>
                        <Button
                          size="compact-xs"
                          variant="light"
                          onClick={() => dictate.confirmMedication(index)}
                          data-testid={`ai-dictate-med-confirm-${index}`}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="default"
                          onClick={() => {
                            setChangingIndex(index)
                            setChangeText(med.normalized)
                          }}
                          data-testid={`ai-dictate-med-change-${index}`}
                        >
                          Change
                        </Button>
                      </>
                    )}
                  </Group>
                ),
              )}
            </Alert>
          )}
        </>
      )}
    </Box>
  )
}
